import React from 'react';
import { Input } from 'reactstrap';
import { useFormikContext } from 'formik';

import Icon, { faMagnifyingGlass } from '~components/icon';

import DateInput from '../inputs/DateInput';
import MultiSelect from '../inputs/MultiSelect';
import Select from '../inputs/Select';
import Time from '../inputs/Time';
import DateRange from './DateRange';
import { SearchProp } from './props';
import TimeRange from './TimeRange';

interface ComponentProps extends SearchProp {
  id: string;
  value: any;
  includeCaption?: boolean;
  className?: string;
  inline?: boolean;
  disabled?: boolean;
  onKeyDown?: (key: string) => void;
  [key: string]: any;
}

const SearchField: React.FC<ComponentProps> = ({
  id,
  value,
  type,
  disabled = false,
  includeCaption = true,
  caption,
  inline = true,
  placeholder,
  className = '',
  selectorOptions,
  options,
  onKeyDown,
  ...props
}) => {
  const { setFieldValue } = useFormikContext();
  let input;
  const inputClassName = `${className}${disabled ? ' bg-disabled' : ''}`;
  switch (type) {
    case 'text':
    case 'text-like': {
      input = (
        <div className={inputClassName}>
          <Input
            type="text"
            name={id}
            id={id}
            className="border-0"
            value={value ?? ''}
            onChange={({ target: { name, value } }) => setFieldValue(name, value)}
            onKeyDown={(event) => onKeyDown && onKeyDown(event.key)}
            placeholder={placeholder}
            disabled={disabled}
            data-cy={`${id}-input`}
          />
          <Icon className="ms-1 me-2" icon={faMagnifyingGlass} />
        </div>
      );
      break;
    }
    case 'lookup-like':
    case 'lookup': {
      input = (
        <div className={inputClassName}>
          {includeCaption && (
            <>
              <span className="ms-2 me-1 fw-bold">{caption}</span>
              <span className="me-1">=</span>
            </>
          )}
          <Select
            {...props}
            type="select"
            name={id}
            id={id}
            value={value ?? ''}
            className={`${!inline ? 'w-100 ps-3' : ''} border-0`}
            onChange={setFieldValue}
            selectorOptions={selectorOptions}
            options={options}
            data-cy={`${id}-lookup`}
            placeholder={placeholder}
            disabled={disabled}
          />
        </div>
      );
      break;
    }
    case 'multi-lookup': {
      input = (
        <div className={inputClassName} data-cy={`${id}-multiselect`}>
          {includeCaption && (
            <>
              <span className="ms-2 me-1 fw-bold">{caption}</span>
              <span className="me-1">=</span>
            </>
          )}
          <MultiSelect
            {...props}
            type="select"
            name={id}
            id={id}
            value={value ?? ''}
            className={`${!inline ? 'w-100' : ''}`}
            selectorOptions={selectorOptions}
            options={options}
            disabled={disabled}
            placeholder={placeholder}
            data-cy={`${id}-multilookup`}
            setFieldValue={setFieldValue}
          />
        </div>
      );
      break;
    }
    case 'date-range': {
      input = (
        <div className={inputClassName}>
          {includeCaption && <span className="ms-2 me-1 fw-bold">{caption}</span>}
          <DateRange
            id={id}
            value={value}
            onChange={setFieldValue}
            disabled={disabled}
            inline={inline}
            {...props}
            data-cy={`${id}-date-range`}
          />
        </div>
      );
      break;
    }
    case 'time-range': {
      input = (
        <div className={inputClassName}>
          {includeCaption && <span className="ms-2 me-1 fw-bold">{caption}</span>}
          <TimeRange
            id={id}
            value={value}
            onChange={setFieldValue}
            onKeyDown={(key: string) => onKeyDown && onKeyDown(key)}
            disabled={disabled}
            inline={inline}
            {...props}
          />
        </div>
      );
      break;
    }
    case 'date': {
      input = (
        <div className={inputClassName}>
          {includeCaption && <span className="ms-2 me-1 fw-bold">{caption}</span>}
          <div className={`${inline ? 'd-inline-flex' : 'w-100 ps-3'}`}>
            <div
              className={`${!inline ? 'mt-1 mb-1' : ''}  d-flex align-items-center ${disabled ? 'bg-disabled' : ''}`}
            >
              <span className="me-2">on</span>
              <DateInput
                type="date"
                name={id}
                id={id}
                className="border-0 m-1"
                value={value}
                disabled={disabled}
                onChange={setFieldValue}
              />
            </div>
          </div>
        </div>
      );
      break;
    }
    case 'time': {
      input = (
        <div className={inputClassName}>
          {includeCaption && <span className="ms-2 me-1 fw-bold">{caption}</span>}
          <div className={`${inline ? 'd-inline-flex' : 'w-100 ps-3'}`}>
            <div
              className={`${!inline ? 'mt-1 mb-1' : ''}  d-flex align-items-center ${disabled ? 'bg-disabled' : ''}`}
            >
              <span className="me-2">at</span>
              <Time
                type="time"
                name={id}
                id={id}
                className={`${disabled ? ' bg-disabled' : ''}`}
                value={value}
                onChange={setFieldValue}
                disabled={disabled}
                data-cy={`${id}-time`}
              />
            </div>
          </div>
        </div>
      );
      break;
    }
    case 'checkbox': {
      input = (
        <div className={inputClassName}>
          {includeCaption && <span className="ms-2 me-1 fw-bold">{caption}</span>}
          <Input
            id={id}
            checked={value}
            type="checkbox"
            className="m-2 p-2"
            onChange={(e) => setFieldValue(id, e.target.checked)}
            {...props}
          />
        </div>
      );
      break;
    }
  }

  return input;
};

export default SearchField;
